/*
 * ---------------------------------------------------------------------------------
 * Copyright:
 *      NewtonGreen Technologies Pty. Ltd.
 *      Level 4, 175 Scott St.
 *      Newcastle, NSW, 2300
 *      Australia
 * 
 *      E-mail: support@newtongreen.com
 *      Tel: (02) 4925 5288
 *      Fax: (02) 4925 3068
 * 
 *      All Rights Reserved.
 * ---------------------------------------------------------------------------------
 */

/*
 * --------------------------------------------------------------------------------
 * This file sets up the logic for retrieving and saving a patient drug.
 * --------------------------------------------------------------------------------
 */

/*
 * ---------------------------------------------------------------------------------
 * Imports - External
 * ---------------------------------------------------------------------------------
 */

/**
 * Used to create logic based side effects for the redux store.
 */
import { createLogic } from 'redux-logic';

/**
 * Typings
 */
import { TypedUseSelectorHook, useSelector, useDispatch } from 'react-redux';

/**
 * Utility library for reducing redux boilerplate while remaining typed. 
 */
import { ImmerReducer, createActionCreators, createReducerFunction } from 'immer-reducer';

/**
 * Enumeration used to determine the state of a request.
 */
import { RequestState, IRequestState } from '@ngt/request-utilities';


import { useState, useEffect } from 'react';

import { bindActionCreators } from 'redux';

/*
 * ---------------------------------------------------------------------------------
 * Imports - Internal
 * ---------------------------------------------------------------------------------
 */

/*
 * Used to register the logics so that they are included in the store. 
 */

import reducerRegistry from '../../reducerRegistry';

/*
 * Used to get access to the API types and requests
 */

import * as Dtos from '../../../api/Neon.Dtos';

import client from '../../../api/client';


/*
* ---------------------------------------------------------------------------------
* Interfaces / Types
* ---------------------------------------------------------------------------------
*/

interface IInitialPatientDrugsState {
    patientDrugs: Dtos.PatientDrug[] | null;
    loadState: IRequestState<Dtos.ResponseStatus>;
    saveState: IRequestState<Dtos.ResponseStatus>;
};

export interface IPatientDrugsState {
    patientDrugs: IInitialPatientDrugsState;
};

/*
 * ---------------------------------------------------------------------------------
 * Initial State
 * ---------------------------------------------------------------------------------
 */

const PatientDrugsInitialState: IInitialPatientDrugsState = {
    patientDrugs: null,
    loadState: {
        state: RequestState.None
    },
    saveState: {
        state: RequestState.None
    }
};

/*
* ---------------------------------------------------------------------------------
* Selectors
* ---------------------------------------------------------------------------------
*/

export const usePatientDrugsSelector: TypedUseSelectorHook<IPatientDrugsState> = useSelector;

export const PatientDrugsSelectors = {
    selectPatientDrugs: (state: IPatientDrugsState) => state.patientDrugs.patientDrugs,
    selectLoadState: (state: IPatientDrugsState) => state.patientDrugs.loadState,
    selectSaveState: (state: IPatientDrugsState) => state.patientDrugs.saveState
};

/*
* ---------------------------------------------------------------------------------
* Reducer
* ---------------------------------------------------------------------------------
*/

class PatientDrugsReducer extends ImmerReducer<IInitialPatientDrugsState> {
    public load() {
        this.draftState.loadState = {
            state: RequestState.Pending
        };
    }

    public loadByPatientId(patientId: number) {
        this.draftState.loadState = {
            state: RequestState.Pending
        };
    }

    public loadSuccess(patientDrugs?: Dtos.PatientDrug[]) {
        this.draftState.patientDrugs = patientDrugs ? patientDrugs : null;
        this.draftState.loadState = {
            state: RequestState.Success
        };
    }

    public loadFailure(responseStatus: Dtos.ResponseStatus) {
        this.draftState.patientDrugs = null;
        this.draftState.loadState = {
            state: RequestState.Failure,
            responseStatus
        };
    }

    public save(form: Dtos.PatientDrug[]) {
        this.draftState.saveState = {
            state: RequestState.Pending
        };
    }

    public saveSuccess(patientDrugs?: Dtos.PatientDrug[]) {
        this.draftState.patientDrugs = patientDrugs ? patientDrugs : null;
        this.draftState.saveState = {
            state: RequestState.Success
        };
    }

    public saveFailure(responseStatus: Dtos.ResponseStatus) {
        this.draftState.patientDrugs = null;
        this.draftState.saveState = {
            state: RequestState.Failure,
            responseStatus
        };
    }

    public clear() {
        this.draftState = { ...PatientDrugsInitialState };
    }
};

export const PatientDrugsActions = createActionCreators(PatientDrugsReducer);
const reducer = createReducerFunction(PatientDrugsReducer, PatientDrugsInitialState);

/*
 * ---------------------------------------------------------------------------------
 * API
 * ---------------------------------------------------------------------------------
 */

export const PatientDrugsApi = {
    load: () => {
        return client.get(new Dtos.GetPatientDrugs());
    },
    loadByPatientId: (patientId: number) => {
        return client.get(new Dtos.GetPatientDrugsByPatientId({
            patientId
        }));
    },
    save: (patientDrugs?: Dtos.PatientDrug[]) => {
        return client.post(new Dtos.SetPatientDrugs({
            patientDrugs
        }));
    }
};

/*
 * ---------------------------------------------------------------------------------
 * Logic
 * ---------------------------------------------------------------------------------
 */

const PatientDrugsLogic = {
    load: createLogic<IPatientDrugsState, {}, undefined, string, ReturnType<typeof PatientDrugsActions.load>>({
        type: PatientDrugsActions.load.type,
        process: async ({ action }, dispatch, done) => {
            try {
                const response = await PatientDrugsApi.load();

                dispatch(PatientDrugsActions.loadSuccess(
                    response.patientDrugs
                ));
            }
            catch (error: any) {
                dispatch(PatientDrugsActions.loadFailure(error ? error.responseStatus : undefined));
            }

            done();
        }
    }),
    loadByPatientId: createLogic<IPatientDrugsState, {}, undefined, string, ReturnType<typeof PatientDrugsActions.loadByPatientId>>({
        type: PatientDrugsActions.loadByPatientId.type,
        process: async ({ action }, dispatch, done) => {
            const patientId = action.payload;

            try {
                const response = await PatientDrugsApi.loadByPatientId(patientId);

                dispatch(PatientDrugsActions.loadSuccess(
                    response.patientDrugs
                ));
            }
            catch (error: any) {
                dispatch(PatientDrugsActions.loadFailure(error ? error.responseStatus : undefined));
            }

            done();
        }
    }),
    save: createLogic<IPatientDrugsState, {}, undefined, string, ReturnType<typeof PatientDrugsActions.save>>({
        type: PatientDrugsActions.save.type,
        process: async ({ action }, dispatch, done) => {
            const patientDrugs = action.payload;

            try {
                const response = await PatientDrugsApi.save(patientDrugs);

                dispatch(PatientDrugsActions.saveSuccess(
                    response.patientDrugs
                ));
            }
            catch (error: any) {
                dispatch(PatientDrugsActions.saveFailure(error ? error.responseStatus : undefined));
            }

            done();
        }
    })
};

const logics = [
    PatientDrugsLogic.load,
    PatientDrugsLogic.loadByPatientId,
    PatientDrugsLogic.save
];

/*
* ---------------------------------------------------------------------------------
* Hooks
* ---------------------------------------------------------------------------------
*/

export const usePatientDrugs = (): [Dtos.PatientDrug[] | null, IRequestState<Dtos.ResponseStatus>, IRequestState<Dtos.ResponseStatus>, typeof PatientDrugsActions] => {
    const patientDrugs = useSelector(PatientDrugsSelectors.selectPatientDrugs);
    const loadState = useSelector(PatientDrugsSelectors.selectLoadState);
    const saveState = useSelector(PatientDrugsSelectors.selectSaveState);

    const dispatch = useDispatch();

    const [actions, setActions] = useState<typeof PatientDrugsActions>(() => {
        return bindActionCreators(PatientDrugsActions, dispatch);
    });

    useEffect(() => {
        setActions(bindActionCreators(PatientDrugsActions, dispatch));
    }, [dispatch, setActions]);

    return [
        patientDrugs,
        loadState,
        saveState,
        actions
    ];
};

/*
 * ---------------------------------------------------------------------------------
 * Register
 * ---------------------------------------------------------------------------------
 */

reducerRegistry.register('patientDrugs', reducer as any, logics as any);